/*
 * VerificarCorreo.jsx — pantalla del enlace del correo (`/verificar?token=...`).
 *
 * ago-2026 · ARREGLO DEL INCIDENTE DE PRODUCCIÓN. Antes cualquier fallo mostraba el mismo
 * "Enlace no válido o expirado". Un usuario real hizo clic tres veces en su enlace: el primer
 * clic lo verificó y los otros dos le dijeron "no válido", así que creyó que había fallado y se
 * quedó pidiendo reenvíos que el backend descartaba en silencio.
 *
 * Ahora el backend devuelve `codigo` (`usado` | `expirado` | `no_encontrado`) y `ya_verificado`,
 * y esta pantalla distingue los casos:
 *   ya verificado → "listo, solo inicia sesión" (verde, sin formulario de reenvío)
 *   sustituido    → "abre el correo más reciente"
 *   expirado/otro → formulario para pedir un enlace nuevo, con el resultado REAL del envío
 */
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Loader2, CheckCircle2, AlertCircle, ArrowRight, RefreshCcw, MailCheck, AlertTriangle, Clock } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AuthShell } from "@/components/auth/AuthShell";
import { toast } from "sonner";

export default function VerificarCorreo() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [estado, setEstado] = useState("validando"); // validando | ok | ya | error
  const [detalle, setDetalle] = useState(null); // {codigo, mensaje, reemplazado}
  const [email, setEmail] = useState("");
  const [reenviando, setReenviando] = useState(false);
  const [resultado, setResultado] = useState(null); // {codigo, mensaje}
  const [espera, setEspera] = useState(0);

  useEffect(() => {
    if (!token) { setEstado("error"); return; }
    api.post("/auth/verificar-correo", { token })
      .then(() => {
        setEstado("ok");
        setTimeout(() => navigate("/login?verificado=1"), 1600);
      })
      .catch((err) => {
        const d = err?.response?.data?.detail;
        const info = (d && typeof d === "object") ? d : { codigo: "no_encontrado", mensaje: apiError(err) };
        setDetalle(info);
        // El enlace ya se usó Y el correo quedó verificado: no hay nada que arreglar.
        setEstado(info.ya_verificado ? "ya" : "error");
      });
  }, [token, navigate]);

  useEffect(() => {
    if (espera <= 0) return;
    const t = setTimeout(() => setEspera((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [espera]);

  const reenviarNuevo = async () => {
    if (!email) {
      toast.error("Escribe tu correo para pedir un enlace nuevo.");
      return;
    }
    setReenviando(true);
    try {
      const { data } = await api.post("/auth/reenviar-verificacion", { email });
      setResultado({ codigo: data?.codigo || "neutral", mensaje: data?.mensaje || "" });
      if (data?.codigo === "espera_60s") setEspera(data?.reintentar_en || 60);
      if (data?.codigo === "fallo_envio") toast.error(data.mensaje);
      else if (data?.codigo === "ya_verificado") { toast.success(data.mensaje); setEstado("ya"); }
      else if (data?.enviado) toast.success(data.mensaje);
      else toast.info(data?.mensaje || "");
    } catch (e) {
      setResultado({ codigo: "error", mensaje: apiError(e) });
      toast.error(apiError(e));
    } finally {
      setReenviando(false);
    }
  };

  return (
    <AuthShell>
      {estado === "validando" && (
        <div data-testid="verificar-validando" className="flex flex-col items-center py-6 text-slate-500">
          <Loader2 className="mb-3 h-6 w-6 animate-spin text-accent" />
          Verificando tu correo…
        </div>
      )}

      {estado === "ok" && (
        <div data-testid="verificar-ok" className="text-center">
          <CheckCircle2 className="mx-auto mb-3 h-10 w-10 text-emerald-500" />
          <h1 className="font-heading text-xl font-semibold text-slate-900">Correo verificado</h1>
          <p className="mt-2 text-sm text-slate-500">Tu correo fue confirmado correctamente.</p>
          <Button data-testid="verificar-ir-login" className="mt-5 w-full bg-primary hover:bg-sidebar-hover"
            onClick={() => navigate("/login")}>
            Continuar <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      )}

      {estado === "ya" && (
        <div data-testid="verificar-ya-verificado" className="text-center">
          <CheckCircle2 className="mx-auto mb-3 h-10 w-10 text-emerald-500" />
          <h1 className="font-heading text-xl font-semibold text-slate-900">Tu correo ya está verificado</h1>
          <p className="mt-2 text-sm text-slate-500">
            Este enlace ya se había usado — y funcionó. No necesitas pedir otro: solo inicia sesión.
          </p>
          <Button data-testid="verificar-ya-ir-login" className="mt-5 w-full bg-primary hover:bg-sidebar-hover"
            onClick={() => navigate("/login")}>
            Ir a iniciar sesión <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      )}

      {estado === "error" && (
        <div data-testid="verificar-error" className="space-y-4 text-center">
          <div>
            <AlertCircle className="mx-auto mb-3 h-10 w-10 text-red-500" />
            <h1 className="font-heading text-xl font-semibold text-slate-900" data-testid="verificar-error-title">
              {detalle?.codigo === "expirado" ? "El enlace expiró"
                : detalle?.reemplazado ? "Este enlace quedó sustituido"
                : detalle?.codigo === "usado" ? "Este enlace ya se usó"
                : "Enlace no válido"}
            </h1>
            <p className="mt-2 text-sm text-slate-500" data-testid="verificar-error-detalle">
              {detalle?.mensaje || "Este enlace de verificación ya no funciona. Pídenos uno nuevo aquí mismo."}
            </p>
          </div>
          <div className="space-y-2 text-left">
            <Input
              data-testid="verificar-error-email"
              type="email"
              placeholder="tu.correo@dominio.mx"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setResultado(null); }}
            />

            {resultado?.codigo === "enviado" && (
              <div className="flex items-center justify-center gap-1.5 rounded-md bg-emerald-50 py-2 text-xs text-emerald-700"
                   data-testid="verificar-error-ok">
                <MailCheck className="h-3.5 w-3.5" /> {resultado.mensaje}
              </div>
            )}
            {resultado?.codigo === "espera_60s" && (
              <div className="flex items-center justify-center gap-1.5 rounded-md bg-amber-50 py-2 text-xs text-amber-700"
                   data-testid="verificar-error-espera">
                <Clock className="h-3.5 w-3.5" />
                {espera > 0 ? `Ya te enviamos un enlace. Puedes pedir otro en ${espera} s.` : resultado.mensaje}
              </div>
            )}
            {(resultado?.codigo === "fallo_envio" || resultado?.codigo === "error") && (
              <div className="flex items-start gap-1.5 rounded-md border border-red-200 bg-red-50 px-2.5 py-2 text-left text-xs text-red-800"
                   data-testid="verificar-error-fallo">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-600" /> {resultado.mensaje}
              </div>
            )}
            {resultado?.codigo === "neutral" && (
              <div className="text-xs text-slate-500" data-testid="verificar-error-neutral">{resultado.mensaje}</div>
            )}

            <Button data-testid="verificar-error-reenviar" type="button" variant="outline"
                    disabled={reenviando || !email || espera > 0} onClick={reenviarNuevo} className="w-full">
              {reenviando ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCcw className="mr-2 h-4 w-4" />}
              {espera > 0 ? `Pedir otro en ${espera} s` : "Enviarme un enlace nuevo"}
            </Button>
          </div>
          <p className="text-sm">
            <Link to="/login" className="font-medium text-accent hover:underline">Volver a iniciar sesión</Link>
          </p>
        </div>
      )}
    </AuthShell>
  );
}
